import { useQuery } from "@tanstack/react-query";
import axios from "axios";

// FX RULE (owner decision, 2026-10-07): a failed rate request must NEVER turn
// into a rate of 1. It used to: the queryFn returned 1 (or {}) on failure and
// react-query cached that for an hour, so HKD was quietly treated as GBP in
// profit, part-exchange and commission figures (about 10x out).
//
// Now:
//  - a failure THROWS, so react-query never caches it as data;
//  - the hook falls back to the LAST KNOWN GOOD rate for the pair (the query's
//    own previous data, else the last success saved in localStorage);
//  - with no known rate at all it returns `rate: null` / `rates: {}` and
//    `unavailable: true`, and each money consumer shows "rate unavailable".
// Same-currency pairs are still exactly 1. That is correct, not a fallback.

const LAST_GOOD_KEY = "rc:fx:last-good:v1";

// In-memory copy so a read never depends on localStorage working (private
// windows and blocked storage throw).
const memory = new Map();

const readStore = () => {
  try {
    const raw = window.localStorage.getItem(LAST_GOOD_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

export const readLastGood = (key) => {
  if (memory.has(key)) return memory.get(key);
  const value = readStore()[key];
  if (value === undefined || value === null) return null;
  memory.set(key, value);
  return value;
};

const writeLastGood = (key, value) => {
  memory.set(key, value);
  try {
    const store = readStore();
    store[key] = value;
    window.localStorage.setItem(LAST_GOOD_KEY, JSON.stringify(store));
  } catch {
    // Storage unavailable: the in-memory copy still covers this session.
  }
};

/** Test helper: forget every remembered rate. */
export const __resetFxLastGood = () => {
  memory.clear();
  try {
    window.localStorage.removeItem(LAST_GOOD_KEY);
  } catch {
    // ignore
  }
};

// A usable rate: a positive finite number. The API may send a numeric string
// (currency_rates.rate is not cast), so the VALUE is returned untouched; this
// only decides whether it counts.
export const isUsableRate = (rate) => {
  if (rate === null || rate === undefined || rate === "") return false;
  const n = Number(rate);
  return Number.isFinite(n) && n > 0;
};

// USDT is pegged 1:1 to USD and has no row of its own in a rates table.
const pegged = (code) => {
  const c = String(code || "").toLowerCase();
  return c === "usdt" ? "usd" : c;
};

/**
 * Rate converting ONE unit of `from` into a useAllFx(target) table's target,
 * or undefined when the table has none. A pegged code (USDT) reads its peg's
 * row (USD). Returns the table's value untouched.
 */
export const tableRate = (rates, from) => {
  const direct = rates?.[String(from || "").toUpperCase()];
  if (isUsableRate(direct)) return direct;
  const peg = rates?.[pegged(from).toUpperCase()];
  return isUsableRate(peg) ? peg : undefined;
};

/**
 * True when converting `amount` of `from` into `target` through `rates`
 * (a useAllFx(target) table) would need a rate the table doesn't have.
 * Zero amounts, same/pegged currencies and rows with no currency (null / "",
 * which were never converted) need none. An omitted (undefined) currency is
 * GBP, matching the `from = "gbp"` default of every convertAmount.
 */
export const needsMissingRate = (rates, target, from, amount) => {
  if (!Number(amount)) return false;
  const ccy = from === undefined ? "gbp" : from;
  if (!ccy) return false;
  if (pegged(ccy) === pegged(target)) return false;
  return tableRate(rates, ccy) === undefined;
};

const isRatesTable = (rates) =>
  !!rates && typeof rates === "object" && !Array.isArray(rates);

const pairKey = (from, to) =>
  `pair:${String(from).toUpperCase()}>${String(to).toUpperCase()}`;
const tableKey = (to) => `all:${String(to).toUpperCase()}`;

export const useFx = (from, to) => {
  const toConverted = to === "USDT" ? "USD" : to;
  const fromConverted = from === "USDT" ? "USD" : from;
  const enabled = !!(from && to && from !== to);
  const key = pairKey(fromConverted, toConverted);

  const query = useQuery({
    queryKey: ["fx", from, to],
    queryFn: async () => {
      // The global interceptor resolves a 5xx with `undefined`, so a missing
      // response is a failure too, not an empty success.
      const response = await axios.get(
        `/api/fx/${fromConverted}/${toConverted}`
      );
      const rate = response?.data?.rate;

      if (!isUsableRate(rate)) {
        throw new Error(`FX rate unavailable for ${fromConverted}/${toConverted}`);
      }

      writeLastGood(key, rate);
      return rate;
    },
    staleTime: 1000 * 60 * 60,
    enabled, // Only run query if parameters are valid
  });

  // Disabled = same currency (or no currency given): exactly 1, as before.
  if (!enabled) {
    return { rate: 1, status: query.status, error: query.error, unavailable: false };
  }

  const known = isUsableRate(query.data) ? query.data : readLastGood(key);
  const rate = isUsableRate(known) ? known : null;

  return {
    rate,
    status: query.status,
    error: query.error,
    // True only once the request has FAILED and no rate was ever known. While
    // the first request is in flight `rate` is null too, but this is false.
    unavailable: rate === null && query.status === "error",
  };
};

export const useAllFx = (to) => {
  const key = tableKey(to);

  const query = useQuery({
    queryKey: ["fx-all", to],
    queryFn: async () => {
      const response = await axios.get(`/api/fx/all/${to}`);
      const rates = response?.data?.rates;

      if (!isRatesTable(rates)) {
        throw new Error(`FX rates unavailable for ${to}`);
      }

      // Remember each usable entry; a null entry never overwrites a good one.
      const good = Object.fromEntries(
        Object.entries(rates).filter(([, r]) => isUsableRate(r))
      );
      if (Object.keys(good).length) {
        writeLastGood(key, { ...(readLastGood(key) || {}), ...good });
      }

      return rates;
    },
    staleTime: 1000 * 60 * 60,
    enabled: !!to, // Only run query if parameter is valid
  });

  // Success: the table exactly as the API sent it. Otherwise the last known
  // good table, else {} — consumers must treat a missing entry as "no rate".
  const lastGood = query.data ? null : readLastGood(key);
  const rates = query.data || lastGood || {};

  return {
    rates,
    status: query.status,
    error: query.error,
    unavailable: !query.data && !lastGood && query.status === "error",
  };
};
